import { formatContentBundleLabel, parseContentBundleBuildDate } from "./contentBundleLabel.js";

export type DeviceSortKey =
  | "name"
  | "serial_number"
  | "status"
  | "fleet"
  | "venue"
  | "content_bundle"
  | "model"
  | "dpc_version"
  | "ip_address"
  | "last_heartbeat_at"
  | "created_at";

export type SortDir = "asc" | "desc";

export type StatusFilter = "all" | "online" | "offline";

export const DEVICE_SORT_KEYS: readonly DeviceSortKey[] = [
  "name",
  "serial_number",
  "status",
  "fleet",
  "venue",
  "content_bundle",
  "model",
  "dpc_version",
  "ip_address",
  "last_heartbeat_at",
  "created_at",
] as const;

export interface SortableDevice {
  name: string;
  serial_number: string;
  status: string;
  fleet_id?: string;
  venue_id?: string;
  model?: string;
  dpc_version?: string;
  ip_address?: string;
  last_heartbeat_at?: string;
  created_at: string;
  config?: Record<string, unknown>;
}

function contentBundleUrl(device: SortableDevice): string {
  return typeof device.config?.content_bundle_url === "string"
    ? device.config.content_bundle_url
    : "";
}

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, "en", { sensitivity: "base", numeric: true });
}

function compareContentBundles(a: SortableDevice, b: SortableDevice): number {
  const aUrl = contentBundleUrl(a);
  const bUrl = contentBundleUrl(b);
  if (!aUrl || !bUrl) return compareStrings(aUrl, bUrl);

  const aDate = parseContentBundleBuildDate(aUrl);
  const bDate = parseContentBundleBuildDate(bUrl);
  // Keep dated builds together so mixing dates and filenames gives a consistent order.
  if (aDate && bDate) {
    return aDate.getTime() - bDate.getTime() || compareStrings(aUrl, bUrl);
  }
  if (aDate || bDate) return aDate ? -1 : 1;
  return (
    compareStrings(formatContentBundleLabel(aUrl), formatContentBundleLabel(bUrl)) ||
    compareStrings(aUrl, bUrl)
  );
}

function compareNullableIso(a: string | undefined, b: string | undefined): number {
  const aMs = a ? Date.parse(a) : Number.NEGATIVE_INFINITY;
  const bMs = b ? Date.parse(b) : Number.NEGATIVE_INFINITY;
  return aMs - bMs;
}

function compareKey(
  a: SortableDevice,
  b: SortableDevice,
  key: DeviceSortKey,
  names: { fleetName: (id?: string) => string; venueName: (id?: string) => string },
): number {
  switch (key) {
    case "name":
      return compareStrings(a.name, b.name);
    case "serial_number":
      // Match the last eight characters displayed by shortSerial in the table.
      return (
        compareStrings(a.serial_number.slice(-8), b.serial_number.slice(-8)) ||
        compareStrings(a.serial_number, b.serial_number)
      );
    case "status":
      return compareStrings(a.status, b.status);
    case "fleet":
      return compareStrings(names.fleetName(a.fleet_id), names.fleetName(b.fleet_id));
    case "venue":
      return compareStrings(names.venueName(a.venue_id), names.venueName(b.venue_id));
    case "content_bundle":
      return compareContentBundles(a, b);
    case "model":
      return compareStrings(a.model ?? "", b.model ?? "");
    case "dpc_version":
      return compareStrings(a.dpc_version ?? "", b.dpc_version ?? "");
    case "ip_address":
      return compareStrings(a.ip_address ?? "", b.ip_address ?? "");
    case "last_heartbeat_at":
      return compareNullableIso(a.last_heartbeat_at, b.last_heartbeat_at);
    case "created_at":
      return compareNullableIso(a.created_at, b.created_at);
  }
}

export function sortDevices<T extends SortableDevice>(
  devices: T[],
  key: DeviceSortKey,
  dir: SortDir,
  names: { fleetName: (id?: string) => string; venueName: (id?: string) => string },
): T[] {
  return [...devices].sort((a, b) => {
    let cmp = compareKey(a, b, key, names);
    if (cmp === 0) cmp = compareStrings(a.name, b.name);
    if (cmp === 0) cmp = compareStrings(a.serial_number, b.serial_number);
    return dir === "asc" ? cmp : -cmp;
  });
}

export function filterByStatus<T extends { status: string }>(
  devices: T[],
  filter: StatusFilter,
): T[] {
  if (filter === "all") return devices;
  if (filter === "online") return devices.filter((d) => d.status === "online");
  return devices.filter((d) => d.status !== "online");
}

export function parseDeviceSortKey(value: string | null): DeviceSortKey | null {
  if (!value) return null;
  return (DEVICE_SORT_KEYS as readonly string[]).includes(value) ? (value as DeviceSortKey) : null;
}

export function parseSortDir(value: string | null): SortDir {
  return value === "desc" ? "desc" : "asc";
}

export function parseStatusFilter(value: string | null): StatusFilter {
  if (value === "online" || value === "offline") return value;
  return "all";
}

export function nextSortState(
  currentKey: DeviceSortKey | null | undefined,
  currentDir: SortDir,
  clicked: DeviceSortKey,
): { key: DeviceSortKey; dir: SortDir } {
  if (currentKey === clicked) {
    return { key: clicked, dir: currentDir === "asc" ? "desc" : "asc" };
  }
  return { key: clicked, dir: "asc" };
}
